import { CheckoutOptimizationStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { t } from "@redotech/redo-i18n";
import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import { isNarySegmentStep } from "../nodes/rco-segment-node";
import { PartialWithRequiredType } from "../steps";

export function stepHasPosteritySplitNode(
  stepId: string,
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[],
) {
  const sourceStep = steps.find((s) => s.id === stepId);
  if (!sourceStep) {
    return false;
  }

  const queue: PartialWithRequiredType<CheckoutOptimizationStep>[] = [
    sourceStep,
  ];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    switch (current.type) {
      case StepType.RCO_SPLIT:
        return true;
      case StepType.CHECKOUT_EXPERIENCE:
      case StepType.BLOCK_CHECKOUT:
        break;
      case StepType.RCO_SEGMENT: {
        if (isNarySegmentStep(current)) {
          for (const edge of current.edges ?? []) {
            const nextStep = steps.find((s) => s.id === edge.nextId);
            if (nextStep) {
              queue.push(nextStep);
            }
          }
        } else {
          const matchingNextStep = steps.find(
            (s) => s.id === current.matchingNextId,
          );
          const nonMatchingNextStep = steps.find(
            (s) => s.id === current.nonMatchingNextId,
          );
          if (matchingNextStep) {
            queue.push(matchingNextStep);
          }
          if (nonMatchingNextStep) {
            queue.push(nonMatchingNextStep);
          }
        }
        break;
      }
      case StepType.TRIGGER: {
        const nextStep = steps.find((s) => s.id === current.nextId);
        if (nextStep) {
          queue.push(nextStep);
        }
        break;
      }
      default:
        assertNever(current);
    }
  }

  return false;
}

export function copySubtree({
  steps,
  sourceNodeId,
  targetNodeId,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  sourceNodeId: string;
  targetNodeId: string;
}):
  | { success: false; errorMessage: string }
  | {
      success: true;
      newSteps: PartialWithRequiredType<CheckoutOptimizationStep>[];
    } {
  const anyNodeInSourceSubtreeIsSplitNode = stepHasPosteritySplitNode(
    sourceNodeId,
    steps,
  );
  const anyNodeInTargetSubtreeIsSplitNode = stepHasPosteritySplitNode(
    targetNodeId,
    steps,
  );

  if (anyNodeInSourceSubtreeIsSplitNode !== anyNodeInTargetSubtreeIsSplitNode) {
    return {
      success: false,
      errorMessage: t(
        "Cannot copy subtree because split node would be duplicated or removed.",
      ),
    };
  }

  const targetSubtreeIds = getSubtreeIds(targetNodeId, steps);
  const sourceSubtreeIds = getSubtreeIds(sourceNodeId, steps);
  if (sourceSubtreeIds.includes(targetNodeId)) {
    return {
      success: false,
      errorMessage: t("Cannot copy subtree because it would create a cycle."),
    };
  }

  if (targetSubtreeIds.includes(sourceNodeId)) {
    // Handle the case where we are cutting out some middle portion of the target subtree
    return {
      success: false,
      errorMessage: t(
        "Not implemented - can't copy a subtree that is already a part of the target subtree.",
      ),
    };
  } else {
    // Handle the case where we are copying to mutually exclusive branches
    const targetStep = steps.find((s) => s.id === targetNodeId);
    if (!targetStep || targetStep.type === StepType.TRIGGER) {
      return { success: false, errorMessage: t("Target node not found.") };
    }

    const parentStepOfTarget = findParentStep(steps, targetNodeId);
    if (!parentStepOfTarget) {
      return { success: false, errorMessage: t("Target node has no parent.") };
    }

    const stepsToCopy = steps.filter((s) => sourceSubtreeIds.includes(s.id));
    const copiedStepIdToNewIdMap = new Map<string, string>(
      stepsToCopy.map((s) => [s.id, new ObjectId().toString()]),
    );

    // For the target's parent node, exchange its nextId pointing to the target to point to the copied source node
    const targetStepNewId = copiedStepIdToNewIdMap.get(sourceNodeId);
    if (!targetStepNewId) {
      throw new Error("Target step new id not found");
    }

    switch (parentStepOfTarget.type) {
      case StepType.TRIGGER:
        parentStepOfTarget.nextId = targetStepNewId;
        break;
      case StepType.RCO_SEGMENT:
        if (isNarySegmentStep(parentStepOfTarget)) {
          const matchingEdge = parentStepOfTarget.edges?.find(
            (e) => e.nextId === targetNodeId,
          );
          if (matchingEdge) {
            matchingEdge.nextId = targetStepNewId;
          } else {
            throw new Error(
              "Target node is not a child of the parent n-ary segment node",
            );
          }
        } else if (parentStepOfTarget.matchingNextId === targetNodeId) {
          parentStepOfTarget.matchingNextId = targetStepNewId;
        } else if (parentStepOfTarget.nonMatchingNextId === targetNodeId) {
          parentStepOfTarget.nonMatchingNextId = targetStepNewId;
        } else {
          throw new Error(
            "Target node is not a child of the parent of the target node",
          );
        }
        break;
      case StepType.RCO_SPLIT: {
        const matchingTreatment = parentStepOfTarget.treatments?.find(
          (t) => t.nextId === targetNodeId,
        );
        if (!matchingTreatment) {
          throw new Error(
            "Target node is not a child of the parent of the target node",
          );
        }

        matchingTreatment.nextId = targetStepNewId;
        break;
      }
      default:
        assertNever(parentStepOfTarget);
    }

    const copiedSteps = stepsToCopy.map((s) => {
      switch (s.type) {
        case StepType.TRIGGER: {
          const newId = copiedStepIdToNewIdMap.get(s.id);
          if (!newId) {
            throw new Error("New id not found for trigger step");
          }

          const newNextId = s.nextId
            ? copiedStepIdToNewIdMap.get(s.nextId)
            : undefined;

          return {
            ...s,
            id: newId,
            ...(newNextId ? { nextId: newNextId } : {}),
          };
        }
        case StepType.RCO_SEGMENT: {
          const newId = copiedStepIdToNewIdMap.get(s.id);
          if (!newId) {
            throw new Error("New id not found for rco segment step");
          }

          if (isNarySegmentStep(s)) {
            const newEdges = s.edges?.map((edge) => ({
              ...edge,
              nextId: edge.nextId
                ? (copiedStepIdToNewIdMap.get(edge.nextId) ?? edge.nextId)
                : edge.nextId,
            }));

            return {
              ...s,
              id: newId,
              ...(newEdges ? { edges: newEdges } : {}),
            };
          }

          const newMatchingNextId = s.matchingNextId
            ? copiedStepIdToNewIdMap.get(s.matchingNextId)
            : undefined;
          const newNonMatchingNextId = s.nonMatchingNextId
            ? copiedStepIdToNewIdMap.get(s.nonMatchingNextId)
            : undefined;

          return {
            ...s,
            id: newId,
            ...(newMatchingNextId ? { matchingNextId: newMatchingNextId } : {}),
            ...(newNonMatchingNextId
              ? { nonMatchingNextId: newNonMatchingNextId }
              : {}),
          };
        }
        case StepType.RCO_SPLIT: {
          const newId = copiedStepIdToNewIdMap.get(s.id);
          if (!newId) {
            throw new Error("New id not found for split step");
          }

          const newTreatments = s.treatments?.map((t) => {
            const newNextId = t.nextId
              ? copiedStepIdToNewIdMap.get(t.nextId)
              : undefined;
            return { ...t, ...(newNextId ? { nextId: newNextId } : {}) };
          });

          return {
            ...s,
            id: newId,
            ...(newTreatments ? { treatments: newTreatments } : {}),
          };
        }
        case StepType.CHECKOUT_EXPERIENCE:
        case StepType.BLOCK_CHECKOUT: {
          const newId = copiedStepIdToNewIdMap.get(s.id);
          if (!newId) {
            throw new Error("New id not found for checkout experience step");
          }

          return { ...s, id: newId };
        }
        default:
          assertNever(s);
      }
    });

    const newSteps = [
      ...steps.filter((s) => !targetSubtreeIds.includes(s.id)),
      ...copiedSteps,
    ];
    return { success: true, newSteps };
  }
}

function getSubtreeIds(
  sourceNodeId: string,
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[],
) {
  const stepIdToStepMap = new Map<
    string,
    PartialWithRequiredType<CheckoutOptimizationStep>
  >();
  for (const step of steps) {
    stepIdToStepMap.set(step.id, step);
  }

  const sourceStep = stepIdToStepMap.get(sourceNodeId);
  if (!sourceStep) {
    return [];
  }

  const queue: PartialWithRequiredType<CheckoutOptimizationStep>[] = [
    sourceStep,
  ];

  const subtreeIds: string[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    subtreeIds.push(current.id);

    switch (current.type) {
      case StepType.TRIGGER: {
        if (!current.nextId) {
          continue;
        }

        const nextStep = stepIdToStepMap.get(current.nextId);
        if (nextStep) {
          queue.push(nextStep);
        }
        break;
      }
      case StepType.RCO_SEGMENT: {
        if (isNarySegmentStep(current)) {
          for (const edge of current.edges ?? []) {
            if (edge.nextId) {
              const nextStep = stepIdToStepMap.get(edge.nextId);
              if (nextStep) {
                queue.push(nextStep);
              }
            }
          }
        } else {
          if (current.matchingNextId) {
            const matchingNextStep = stepIdToStepMap.get(
              current.matchingNextId,
            );
            if (matchingNextStep) {
              queue.push(matchingNextStep);
            }
          }

          if (current.nonMatchingNextId) {
            const nonMatchingNextStep = stepIdToStepMap.get(
              current.nonMatchingNextId,
            );
            if (nonMatchingNextStep) {
              queue.push(nonMatchingNextStep);
            }
          }
        }
        break;
      }
      case StepType.RCO_SPLIT: {
        for (const treatment of current.treatments ?? []) {
          if (!treatment.nextId) {
            continue;
          }

          const nextStep = stepIdToStepMap.get(treatment.nextId);
          if (nextStep) {
            queue.push(nextStep);
          }
        }
        break;
      }
      case StepType.CHECKOUT_EXPERIENCE:
      case StepType.BLOCK_CHECKOUT: {
        break;
      }
      default:
        assertNever(current);
    }
  }

  return subtreeIds;
}

function findParentStep(
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[],
  stepId: string,
) {
  for (const step of steps) {
    switch (step.type) {
      case StepType.TRIGGER:
        if (step.nextId === stepId) {
          return step;
        }
        break;
      case StepType.RCO_SEGMENT:
        if (isNarySegmentStep(step)) {
          if (step.edges?.some((e) => e.nextId === stepId)) {
            return step;
          }
        } else if (
          step.matchingNextId === stepId ||
          step.nonMatchingNextId === stepId
        ) {
          return step;
        }
        break;
      case StepType.RCO_SPLIT: {
        const matchingTreatment = step.treatments?.find(
          (t) => t.nextId === stepId,
        );
        if (matchingTreatment) {
          return step;
        }
        break;
      }
      case StepType.CHECKOUT_EXPERIENCE:
      case StepType.BLOCK_CHECKOUT:
        break;
      default:
        assertNever(step);
    }
  }

  return undefined;
}
